import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  Filter,
  CalendarDays,
  FileText,
  Download,
  RotateCcw,
  ChevronDown,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { DateRange } from "react-day-picker";
import { useUserInfo } from "@/hooks/use-user-info";
import { isProgramAdmin } from "@/utils/role.utils";

interface TransferTransactionFiltersProps {
  onFilterChange: (filters: any) => void;
  onExport: (format: "csv" | "csv-all-programs" | "pdf") => void;
  onDateRangeChange: (range: string) => void;
  onReset: () => void;
  programs: string[];
  transferPartners?: any[];
  allPrograms?: any[];
  searchTerm: string;
  onSearch: (term: string) => void;
  resetKey?: any; // new prop to trigger UI reset
  initialTransferPartnerId?: string;
}

const FILTER_CATEGORIES = [
  { label: "Program", value: "transferPartner", isArray: false },
  { label: "Status", value: "status", isArray: true },
  // Transfer Partner filter hidden from advanced filter popup
  {
    label: "Transfer Transaction Id",
    value: "transferPartnerTransactionId",
    isArray: false,
  },
];

const STATUS_OPTIONS = [
  { label: "Accrual Success", value: "ACCRUAL_SUCCESS" },
  { label: "Accrual Pending", value: "PENDING" },
  { label: "Accrual Failed", value: "FAILED" },
  { label: "Redemption Success", value: "REDEMPTION_SUCCESS" },
];

export const TransferTransactionFilters: React.FC<
  TransferTransactionFiltersProps
> = ({
  onFilterChange,
  onExport,
  onDateRangeChange,
  onReset,
  programs = [],
  transferPartners = [],
  allPrograms = [],
  searchTerm,
  onSearch,
  resetKey,
  initialTransferPartnerId,
}) => {
  const { userInfo } = useUserInfo();
  const programAdmin = isProgramAdmin(userInfo?.roleCode);
  const [dateRange, setDateRange] = useState("");
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("status");
  // Add startDate and endDate to filterSelections
  const [filterSelections, setFilterSelections] = useState<any>({
    status: [] as string[],
    program: "",
    transferPartner: "",
    transferPartnerTransactionId: "",
    startDate: "",
    endDate: "",
  });
  const [pendingFilterSelections, setPendingFilterSelections] = useState<any>({
    status: [] as string[],
    program: "",
    transferPartner: "",
    transferPartnerTransactionId: "",
    startDate: "",
    endDate: "",
  });
  const [searchInputs, setSearchInputs] = useState<any>({
    status: "",
    program: "",
    transferPartner: "",
  });
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(
    undefined,
  );

  const isFilterActive = (key: string, value: unknown) => {
    if (key === "status") {
      return Array.isArray(value) && value.length > 0;
    }
    return Boolean(value);
  };

  // Count of active filters (empty status array must not count as applied)
  // "program" excluded — Transfer Partner category is hidden from the popup
  const activeFilterCount = [
    "status",
    "transferPartner",
    "transferPartnerTransactionId",
  ].reduce(
    (acc, key) => acc + (isFilterActive(key, filterSelections[key]) ? 1 : 0),
    0,
  );

  // Watch for parent reset and clear all filter states
  useEffect(() => {
    setCustomDateRange(undefined);
    setDateRange("");
    setFilterSelections({
      status: [],
      program: "",
      transferPartner: "",
      transferPartnerTransactionId: "",
      startDate: "",
      endDate: "",
    });
    setPendingFilterSelections({
      status: [],
      program: "",
      transferPartner: "",
      transferPartnerTransactionId: "",
      startDate: "",
      endDate: "",
    });
    setSearchInputs({
      status: "",
      program: "",
      transferPartner: "",
    });
  }, [resetKey]);

  useEffect(() => {
    if (initialTransferPartnerId && !filterSelections.transferPartner) {
      const newFilterSelections = {
        ...filterSelections,
        transferPartner: initialTransferPartnerId,
      };
      const newPendingFilterSelections = {
        ...pendingFilterSelections,
        transferPartner: initialTransferPartnerId,
      };
      setFilterSelections(newFilterSelections);
      setPendingFilterSelections(newPendingFilterSelections);

      onFilterChange({
        transferPartnerId: initialTransferPartnerId,
        transferStatuses: [],
      });
    }
    // eslint-disable-next-line
  }, [initialTransferPartnerId]);

  useEffect(() => {
    if (filterPopoverOpen) {
      setPendingFilterSelections({ ...filterSelections });
    }
  }, [filterPopoverOpen]);

  const buildFilterPayload = (
    selections: any,
    options?: {
      clearFields?: Array<
        | "status"
        | "program"
        | "transferPartner"
        | "transferPartnerTransactionId"
      >;
    },
  ) => {
    const clearFields = options?.clearFields || [];
    // Do NOT send empty programCode — top page dropdown owns programCode.
    // Sending programCode: "" overwrites it and skips the API (no programId).
    const payload: any = {
      transferStatuses: Array.isArray(selections.status)
        ? selections.status
        : selections.status
          ? [selections.status]
          : [],
      transferPartnerTransactionId:
        selections.transferPartnerTransactionId || "",
    };

    if (selections.program) {
      payload.programCode = selections.program;
    }

    if (selections.transferPartner) {
      payload.transferPartnerId = selections.transferPartner;
    } else if (clearFields.includes("transferPartner")) {
      payload.transferPartnerId = "";
    }

    return payload;
  };

  const applyFilters = () => {
    setFilterSelections({ ...pendingFilterSelections });
    onFilterChange(buildFilterPayload(pendingFilterSelections));
    setFilterPopoverOpen(false);
  };

  const handleOptionSelect = (category: string, value: string) => {
    if (category === "status") {
      setPendingFilterSelections((prev: any) => {
        const current = Array.isArray(prev.status) ? prev.status : [];
        const exists = current.includes(value);
        return {
          ...prev,
          status: exists
            ? current.filter((s: string) => s !== value)
            : [...current, value],
        };
      });
      return;
    }
    setPendingFilterSelections((prev: any) => ({
      ...prev,
      [category]: prev[category] === value ? "" : value,
    }));
  };

  const handleSearchInput = (category: string, value: string) => {
    setSearchInputs((prev: any) => ({ ...prev, [category]: value }));
  };

  // Clear a single filter category in UI + payload immediately (same as Access Point)
  const handleClearCategory = (category: string) => {
    const nextSelections = { ...filterSelections };
    const nextPending = { ...pendingFilterSelections };

    if (category === "status") {
      nextSelections.status = [];
      nextPending.status = [];
    } else {
      nextSelections[category] = "";
      nextPending[category] = "";
    }

    setFilterSelections(nextSelections);
    setPendingFilterSelections(nextPending);

    if (category !== "status") {
      setSearchInputs((prev: any) => ({ ...prev, [category]: "" }));
    }

    onFilterChange(
      buildFilterPayload(nextSelections, {
        clearFields: [category as "status" | "program" | "transferPartner" | "transferPartnerTransactionId"],
      }),
    );
  };

  const handleClearAll = () => {
    const cleared = {
      status: [] as string[],
      program: "",
      transferPartner: "",
      transferPartnerTransactionId: "",
    };
    setFilterSelections(cleared);
    setPendingFilterSelections(cleared);
    setSearchInputs({ status: "", program: "", transferPartner: "" });
    // Preserve page-level programCode; only clear advanced filter fields
    onFilterChange({
      transferStatuses: [],
      transferPartnerId: "",
      transferPartnerTransactionId: "",
    });
  };

  const handleClearCurrent = () => {
    handleClearCategory(activeCategory);
  };

  // Handler for custom date range
  const handleCustomDateRangeChange = (range: DateRange | undefined) => {
    setCustomDateRange(range);
    if (range && range.from && range.to) {
      // Set start date to beginning of day (00:00:00)
      const startDate = new Date(range.from);
      startDate.setHours(0, 0, 0, 0);
      const startDateInMillis = startDate.getTime();

      // Set end date to end of day (23:59:59)
      const endDate = new Date(range.to);
      endDate.setHours(23, 59, 59, 999);
      const endDateInMillis = endDate.getTime();

      // Only send the new date values
      onFilterChange({ startDateInMillis, endDateInMillis });
    }
  };

  const renderOptions = () => {
    switch (activeCategory) {
      case "status":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {STATUS_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={
                    Array.isArray(pendingFilterSelections.status) &&
                    pendingFilterSelections.status.includes(opt.value)
                  }
                  onChange={() => handleOptionSelect("status", opt.value)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600"
                />
                <span className="text-xs font-medium">{opt.label}</span>
              </label>
            ))}
            {Array.isArray(pendingFilterSelections.status) &&
              pendingFilterSelections.status.length > 0 && (
                <button
                  className="text-xs text-gray-500 hover:underline mt-2 self-start"
                  onClick={() => handleClearCategory("status")}
                  type="button"
                >
                  Clear
                </button>
              )}
          </div>
        );
      case "program":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {Array.isArray(allPrograms) && allPrograms.length > 0 ? (
              <>
                {allPrograms
                  .filter((p: any) => {
                    const programName = p?.programName || "";
                    const searchTerm = (
                      searchInputs.program || ""
                    ).toLowerCase();
                    return programName.toLowerCase().includes(searchTerm);
                  })
                  .map((program: any) => (
                    <label
                      key={program.programCode}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="program-filter"
                        checked={
                          String(pendingFilterSelections.program) ===
                          String(program.programCode)
                        }
                        onChange={() =>
                          handleOptionSelect("program", program.programCode)
                        }
                        className="form-radio h-4 w-4 text-blue-600"
                      />
                      <span className="text-xs font-medium">
                        {program.programName || program.programCode || "N/A"}
                      </span>
                    </label>
                  ))}
                {pendingFilterSelections.program && (
                  <button
                    className="text-xs text-gray-500 hover:underline mt-2 self-start"
                    onClick={() => handleClearCategory("program")}
                    type="button"
                  >
                    Clear
                  </button>
                )}
              </>
            ) : (
              <div className="text-xs text-gray-500 py-4 text-center">
                No programs available
              </div>
            )}
          </div>
        );
      case "transferPartner":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {transferPartners
              .filter((tp: any) =>
                (tp.name || "")
                  .toLowerCase()
                  .includes((searchInputs.transferPartner || "").toLowerCase()),
              )
              .map((tp: any) => (
                <label
                  key={tp.id}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="transfer-partner-filter"
                    checked={
                      String(pendingFilterSelections.transferPartner) ===
                      String(tp.id)
                    }
                    onChange={() =>
                      handleOptionSelect("transferPartner", String(tp.id))
                    }
                    className="form-radio h-4 w-4 text-blue-600"
                  />
                  <span className="text-xs font-medium">{tp.name}</span>
                </label>
              ))}
            {pendingFilterSelections.transferPartner && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleClearCategory("transferPartner")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      case "transferPartnerTransactionId":
        return (
          <div className="flex flex-col gap-2">
            <Input
              placeholder="Enter id"
              value={pendingFilterSelections.transferPartnerTransactionId || ""}
              onChange={(e) =>
                setPendingFilterSelections((prev: any) => ({
                  ...prev,
                  transferPartnerTransactionId: e.target.value,
                }))
              }
              className="h-8 text-xs"
            />
            {pendingFilterSelections.transferPartnerTransactionId ? (
              <button
                type="button"
                className="text-xs text-gray-500 hover:underline mt-1 self-start"
                onClick={() =>
                  handleClearCategory("transferPartnerTransactionId")
                }
              >
                Clear
              </button>
            ) : null}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Card className="mb-6 border-0 shadow-sm">
      <CardContent className="p-4">
        <div className="flex flex-col gap-4">
          {/* Toolbar: Date, Filter Button (search removed) */}
          <div className="flex flex-wrap gap-3 items-center justify-between mb-2">
            <div className="flex gap-3 items-center w-full">
              {/* Only show input for Transaction ID search */}
              <Input
                className="w-64"
                type="text"
                placeholder="Search by Transaction ID"
                value={searchTerm}
                onChange={(e) => onSearch(e.target.value)}
              />
              <div className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-gray-500" />
                <Select
                  onValueChange={(v) => {
                    setDateRange(v);
                    onDateRangeChange(v);
                  }}
                  value={dateRange}
                >
                  <SelectTrigger className="h-10 w-40">
                    <SelectValue placeholder="Choose Date" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Choose Date</SelectItem>
                    <SelectItem value="30days">Last 30 Days</SelectItem>
                    <SelectItem value="7days">Last 7 Days</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="yesterday">Yesterday</SelectItem>
                    <SelectItem value="thisMonth">This Month</SelectItem>
                    <SelectItem value="lastMonth">Last Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Add custom date range picker here */}
              <div className="w-64 h-10 flex items-center">
                <DatePickerWithRange
                  date={customDateRange}
                  setDate={handleCustomDateRangeChange}
                  placeholder="Select start and end date"
                />
              </div>
              <div className="flex gap-2 items-center ml-auto">
                <Popover
                  open={filterPopoverOpen}
                  onOpenChange={setFilterPopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant={activeFilterCount > 0 ? "default" : "outline"}
                      className="flex items-center gap-2 px-3 py-2 text-sm"
                      onClick={() => setFilterPopoverOpen(true)}
                      size="sm"
                    >
                      <Filter className="h-4 w-4" />
                      Filter
                      {activeFilterCount > 0 && (
                        <span className="ml-1 bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                          {activeFilterCount}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[340px] p-0"
                    side="left"
                    align="center"
                    sideOffset={8}
                  >
                    <div className="flex max-h-[320px] h-[320px]">
                      {/* Left: Categories */}
                      <div className="w-32 border-r bg-gray-50 p-2 flex flex-col gap-1">
                        {FILTER_CATEGORIES.map((cat) => {
                          const hasSelection = cat.isArray
                            ? Array.isArray(filterSelections[cat.value]) &&
                              filterSelections[cat.value].length > 0
                            : filterSelections[cat.value] &&
                              filterSelections[cat.value] !== "";
                          return (
                            <button
                              key={cat.value}
                              className={`text-left px-2 py-1 rounded hover:bg-gray-100 font-medium text-xs ${
                                activeCategory === cat.value
                                  ? "bg-white shadow text-blue-600"
                                  : "text-gray-700"
                              }`}
                              onClick={() => setActiveCategory(cat.value)}
                            >
                              {cat.label}
                              {cat.isArray &&
                              Array.isArray(filterSelections[cat.value]) &&
                              filterSelections[cat.value].length > 0 ? (
                                <span className="ml-2 bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5 text-[10px] font-semibold">
                                  {filterSelections[cat.value].length}
                                </span>
                              ) : hasSelection ? (
                                <span className="ml-2 bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5 text-[10px] font-semibold">
                                  ✓
                                </span>
                              ) : null}
                            </button>
                          );
                        })}
                        <div className="flex-1" />
                        <button
                          className="text-[10px] text-gray-500 hover:underline mt-2"
                          onClick={handleClearAll}
                          type="button"
                        >
                          Clear all
                        </button>
                      </div>
                      {/* Right: Options for selected category */}
                      <div className="flex-1 p-2 flex flex-col min-h-0">
                        <div className="flex items-center mb-1">
                          {activeCategory !== "status" &&
                            activeCategory !==
                              "transferPartnerTransactionId" && (
                              <Input
                                placeholder={
                                  activeCategory === "program"
                                    ? "Search transfer partner"
                                    : activeCategory === "transferPartner"
                                      ? "Search program"
                                      : `Search ${activeCategory}`
                                }
                                value={searchInputs[activeCategory] || ""}
                                onChange={(e) =>
                                  handleSearchInput(
                                    activeCategory,
                                    e.target.value,
                                  )
                                }
                                className="h-7 w-full text-xs"
                              />
                            )}
                          {activeCategory !== "status" &&
                            Array.isArray(filterSelections[activeCategory]) &&
                            filterSelections[activeCategory].length > 0 && (
                              <button
                                className="ml-2 text-[10px] text-gray-500 hover:underline"
                                onClick={handleClearCurrent}
                                type="button"
                              >
                                Clear
                              </button>
                            )}
                        </div>
                        <div className="text-xs font-medium flex-1 min-h-0 overflow-y-auto gap-1 flex flex-col">
                          {renderOptions()}
                        </div>
                        <div className="flex justify-end mt-2">
                          <Button
                            size="sm"
                            className="text-xs px-3 py-1 h-7"
                            onClick={applyFilters}
                          >
                            Apply
                          </Button>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2 px-3 py-2 text-sm"
                  onClick={onReset}
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2 px-3 py-2 text-sm"
                    >
                      <Download className="h-4 w-4" />
                      Export
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-2" align="end">
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start text-sm"
                        onClick={() => onExport("csv")}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        <span>CSV</span>
                      </Button>
                      {!programAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="justify-start text-sm"
                          onClick={() => onExport("csv-all-programs")}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          <span>CSV (All Programs)</span>
                        </Button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
